import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { PriceChange, Schedule } from '@mui/icons-material';
import {
  IngestionBrandInfo,
  ScheduleFrequency,
  UpdateSchedulePayload,
} from '../../services/integrationApi';
import { PRICE_REFRESH_SCHEDULE_LABEL } from './connectorHealth';
import { NumberField, parseNumberInput } from '../ui';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'UTC',
];

interface ScheduleDialogProps {
  open: boolean;
  brand: IngestionBrandInfo | null;
  onClose: () => void;
  onSave: (brandId: string, payload: UpdateSchedulePayload) => void;
  onRunPriceRefresh: (brandId: string, region: 'US' | 'CA') => void;
  isPending: boolean;
  isPriceRefreshPending: boolean;
  priceRefreshMessage?: string | null;
  priceRefreshError?: string | null;
  priceRefreshDisabled?: boolean;
}

export const ScheduleDialog: React.FC<ScheduleDialogProps> = ({
  open,
  brand,
  onClose,
  onSave,
  onRunPriceRefresh,
  isPending,
  isPriceRefreshPending,
  priceRefreshMessage,
  priceRefreshError,
  priceRefreshDisabled,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [frequency, setFrequency] = useState<ScheduleFrequency>(brand?.schedule?.frequency || 'weekly');
  const [isEnabled, setIsEnabled] = useState(brand?.schedule?.isEnabled || false);
  // The stored preference; it only runs while the schedule itself is on.
  const [priceRefreshEnabled, setPriceRefreshEnabled] = useState(
    brand?.schedule?.priceRefreshEnabled !== false
  );
  const [preferredTime, setPreferredTime] = useState(brand?.schedule?.preferredTime || '03:00');
  const [preferredDay, setPreferredDay] = useState<number>(brand?.schedule?.preferredDay ?? 0);
  // Day of month: the string the box shows is the state; the number is derived at save.
  const [dayText, setDayText] = useState<string>(String(brand?.schedule?.preferredDay || 1));
  const [timezone, setTimezone] = useState(brand?.schedule?.timezone || 'America/Denver');
  const [priceRefreshRegion, setPriceRefreshRegion] = useState<'US' | 'CA'>('US');

  useEffect(() => {
    if (brand) {
      setFrequency(brand.schedule?.frequency || 'weekly');
      setIsEnabled(brand.schedule?.isEnabled || false);
      setPriceRefreshEnabled(brand.schedule?.priceRefreshEnabled !== false);
      setPreferredTime(brand.schedule?.preferredTime || '03:00');
      setPreferredDay(brand.schedule?.preferredDay ?? 0);
      setDayText(String(brand.schedule?.preferredDay || 1));
      setTimezone(brand.schedule?.timezone || 'America/Denver');
    }
  }, [brand]);

  const dayParsed = parseNumberInput(dayText);
  const dayInvalid =
    frequency === 'monthly' &&
    isEnabled &&
    (dayParsed === null || !Number.isInteger(dayParsed) || dayParsed < 1 || dayParsed > 28);

  const handleSave = () => {
    if (!brand || dayInvalid) return;
    onSave(brand.id, {
      frequency,
      isEnabled,
      priceRefreshEnabled,
      preferredTime,
      preferredDay:
        frequency === 'monthly'
          ? (dayParsed ?? undefined)
          : frequency === 'weekly'
            ? preferredDay
            : undefined,
      timezone,
    });
  };

  if (!brand) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <Schedule />
          <span>Schedule Settings: {brand.name}</span>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <PriceChange sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="subtitle2">Daily Price Refresh</Typography>
          </Stack>
          <FormControlLabel
            control={
              <Switch
                checked={priceRefreshEnabled && isEnabled}
                disabled={!isEnabled}
                onChange={(e) => setPriceRefreshEnabled(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography>
                {!isEnabled
                  ? 'Paused with the schedule'
                  : priceRefreshEnabled
                  ? `Included in scheduled price refresh (${PRICE_REFRESH_SCHEDULE_LABEL})`
                  : 'Excluded from scheduled price refresh'}
              </Typography>
            }
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Re-checks source pricing for ingested products without re-scraping full product content.
            {!isEnabled && ' Turn the full scrape schedule back on to resume it.'}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Region</InputLabel>
              <Select
                value={priceRefreshRegion}
                label="Region"
                onChange={(e) => setPriceRefreshRegion(e.target.value as 'US' | 'CA')}
              >
                <MenuItem value="US">US (USD)</MenuItem>
                <MenuItem value="CA">Canada (CAD)</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={isPriceRefreshPending ? <CircularProgress size={16} /> : <PriceChange />}
              onClick={() => onRunPriceRefresh(brand.id, priceRefreshRegion)}
              disabled={
                isPriceRefreshPending ||
                priceRefreshDisabled ||
                brand.isRunning ||
                brand.scraperStatus === 'coming_soon'
              }
            >
              {isPriceRefreshPending ? 'Starting...' : 'Run Price Refresh Now'}
            </Button>
          </Stack>
          {priceRefreshError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {priceRefreshError}
            </Alert>
          )}
          {priceRefreshMessage && (
            <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
              {priceRefreshMessage}
            </Typography>
          )}
        </Box>

        <Divider />

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Full Scrape Schedule
          </Typography>
          <FormControlLabel
            control={
              <Switch checked={isEnabled} onChange={(e) => setIsEnabled(e.target.checked)} color="primary" />
            }
            label={
              <Typography>
                {isEnabled ? 'Scheduled full scrape & discovery enabled' : 'Scheduled full scrape & discovery disabled'}
              </Typography>
            }
          />

          <FormControl fullWidth disabled={!isEnabled} sx={{ mt: 1.5 }}>
            <InputLabel>Frequency</InputLabel>
            <Select
              value={frequency}
              label="Frequency"
              onChange={(e) => setFrequency(e.target.value as ScheduleFrequency)}
            >
              <MenuItem value="manual">Manual Only</MenuItem>
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
            </Select>
          </FormControl>

          {frequency === 'weekly' && isEnabled && (
            <FormControl fullWidth sx={{ mt: 1.5 }}>
              <InputLabel>Day of Week</InputLabel>
              <Select
                value={preferredDay}
                label="Day of Week"
                onChange={(e) => setPreferredDay(Number(e.target.value))}
              >
                {DAYS_OF_WEEK.map((day) => (
                  <MenuItem key={day.value} value={day.value}>
                    {day.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {frequency === 'monthly' && isEnabled && (
            <NumberField
              label="Day of Month"
              integer
              min={1}
              max={28}
              allowEmpty={false}
              value={dayText}
              onChange={setDayText}
              helperText="1-28 (to avoid month-end issues)"
              size="medium"
              sx={{ mt: 1.5, width: '100%' }}
            />
          )}

          {frequency !== 'manual' && isEnabled && (
            <>
              <TextField
                label="Preferred Time"
                type="time"
                value={preferredTime}
                onChange={(e) => setPreferredTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Time when the scrape will run"
                fullWidth
                sx={{ mt: 1.5 }}
              />

              <FormControl fullWidth sx={{ mt: 1.5 }}>
                <InputLabel>Timezone</InputLabel>
                <Select value={timezone} label="Timezone" onChange={(e) => setTimezone(e.target.value)}>
                  {TIMEZONES.map((tz) => (
                    <MenuItem key={tz} value={tz}>
                      {tz.replace('_', ' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}

          {brand.schedule?.nextScheduledRun && isEnabled && (
            <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1, mt: 1.5 }}>
              <Typography variant="body2">
                <strong>Next scheduled run:</strong>{' '}
                {new Date(brand.schedule.nextScheduledRun).toLocaleString()}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isPending || dayInvalid}
          startIcon={isPending ? <CircularProgress size={16} /> : null}
        >
          {isPending ? 'Saving...' : 'Save Schedule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleDialog;
